import { oktaAuth } from './okta.config';
import {
  OKTA_CANCEL_MESSAGE,
  OKTA_GENERIC_FAILURE_MESSAGE,
} from './okta.messages';

export { OKTA_CANCEL_MESSAGE, OKTA_GENERIC_FAILURE_MESSAGE } from './okta.messages';

const OKTA_STORAGE_KEYS = [
  'okta-token-storage',
  'okta-cache-storage',
  'okta-pkce-storage',
  'okta-transaction-storage',
  'okta-shared-transaction-storage',
  'okta-original-uri-storage',
  'okta-idx-response-storage',
];

function clearStorageKeys(storage) {
  if (!storage) return;
  OKTA_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
}

function clearStorageProvider(getStorage) {
  const storage = getStorage?.();
  storage?.clearStorage?.();
}

export function clearOktaStorage() {
  try {
    oktaAuth.tokenManager?.clear?.();
    oktaAuth.transactionManager?.clear?.();
    clearStorageProvider(() => oktaAuth.storageManager?.getHttpCache?.());
    clearStorageProvider(() => oktaAuth.storageManager?.getOriginalUriStorage?.());
    clearStorageProvider(() => oktaAuth.storageManager?.getSharedTansactionStorage?.());
  } catch {
    // Logout should still succeed even if Okta SDK cleanup fails.
  }

  clearStorageKeys(window.localStorage);
  clearStorageKeys(window.sessionStorage);
}

/**
 * True when the current URL looks like an Okta authorize callback
 * (`/login?code=...` or Okta error params).
 */
export function isOktaCallback() {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
}

/**
 * Starts the Okta authorize redirect (Auth Code + PKCE).
 *
 * @param {{ loginHint?: string }} [options]
 * @returns {Promise<void>}
 */
export async function loginWithOktaTokenAsync(options = {}) {
  const { loginHint } = options;
  const redirectOptions = {};
  const trimmedLoginHint = loginHint?.trim();

  if (trimmedLoginHint) {
    redirectOptions.loginHint = trimmedLoginHint;
  }

  await oktaAuth.signInWithRedirect(redirectOptions);
}

/**
 * Completes the PKCE code → tokens exchange after Okta redirects back.
 * Returns the id_token JWT string for POST to the backend.
 * Does not set app session identity.
 *
 * @returns {Promise<string>}
 */
export async function completeOktaCodeExchangeAsync() {
  const { tokens } = await oktaAuth.token.parseFromUrl();
  const idToken = tokens?.idToken?.idToken;

  if (!idToken) {
    throw new Error('Okta response did not include an id_token');
  }

  return idToken;
}

/**
 * Reads Okta error query params from the current URL.
 * @returns {{ error: string, description: string } | null}
 */
export function getOktaCallbackError() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (!error) return null;
  return { error, description: params.get('error_description') || error };
}

/**
 * Maps Okta authorize callback error params to friendly UX copy.
 * Does not surface raw Okta error_description strings.
 *
 * @param {{ error: string, description: string }} callbackError
 * @returns {string}
 */
export function getOktaCallbackUserMessage(callbackError) {
  const code = callbackError?.error?.toLowerCase?.() || '';
  const description = callbackError?.description?.toLowerCase?.() || '';
  const isCancel = code === 'access_denied'
    || description.includes('cancel')
    || description.includes('abort');

  if (isCancel) {
    return OKTA_CANCEL_MESSAGE;
  }

  return OKTA_GENERIC_FAILURE_MESSAGE;
}

/**
 * Strips Okta callback query params from the URL so a refresh
 * does not re-process the same authorization code.
 */
export function clearOktaCallbackParams() {
  const { pathname, hash } = window.location;
  window.history.replaceState(null, '', pathname + hash);
}
